import { toast } from '@/core/hooks/use-toast';
import { appConfig } from '@/core/config';
import { servicesContainer } from '@/core/di/services-container';
import { tokenRefreshManager, TokenRefreshError } from '@/core/auth/token-refresh-manager';

/**
 * A 401 from a service that doesn't use the back-office session — the user is still logged in
 */
export class ApiUnauthorizedError extends Error {
  constructor(apiName: string, endpoint?: string) {
    super(`${apiName} rejected the request (401)${endpoint ? `: ${endpoint}` : ''}`);
    this.name = 'ApiUnauthorizedError';
  }
}

/**
 * API Error Middleware
 * Handles common error scenarios and shows debug toasts in development mode
 */
export class ApiErrorMiddleware {
  /**
   * Handles API response errors and shows appropriate debug toasts
   * @param response - The fetch response object
   * @param apiName - Name of the API for debugging (e.g., "Auth API", "Kiosk API")
   * @param endpoint - The endpoint that was called for debugging
   * @param suppressToastForStatus - Array of status codes to suppress toast for
   */
  static async handleErrorResponse(
    response: Response,
    apiName: string,
    endpoint?: string,
    suppressToastForStatus?: number[]
  ): Promise<string> {
    const errorData = await response.json().catch(() => null);
    let errorMessage = `${apiName} request failed`;
    
    // Handle 401 first - token expiration takes priority
    if (response.status === 401) {
      // Don't clear tokens immediately - wait for new token from parent iframe
      try {
        await tokenRefreshManager.waitForToken(5000);
        // Token received! Throw special error so caller knows to retry
        throw new TokenRefreshError('Token refreshed - retry request');
      } catch (error) {
        if (error instanceof TokenRefreshError) {
          throw error; // Let caller handle retry
        }
        // Timeout or other error - clear tokens and show error as before
        errorMessage = 'Session expired. Please login again.';
        await this.handleExpiredToken();
      }
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    } else if (errorData?.errorMessage) {
      errorMessage = errorData.errorMessage;
    } else if (response.status === 403) {
      errorMessage = 'Access denied. Insufficient permissions.';
    } else if (response.status === 404) {
      errorMessage = 'Endpoint not found.';
    } else if (response.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }


    // Show debug toasts in development mode (unless suppressed for this status code)
    const shouldSuppressToast = suppressToastForStatus?.includes(response.status);
    if (appConfig.isDevelopment() && !shouldSuppressToast) {
      this.showDebugToast(response.status, errorMessage, apiName, endpoint);
    }

    return errorMessage;
  }

  /**
   * Shows debug toast notifications for different error types
   */
  private static showDebugToast(
    status: number, 
    errorMessage: string, 
    apiName: string, 
    endpoint?: string
  ): void {
    const endpointInfo = endpoint ? ` (${endpoint})` : '';
    
    if (status >= 500) {
      toast({
        title: "🔥 Server Error (500)",
        description: `${apiName}${endpointInfo}: ${errorMessage}`,
        variant: "destructive",
      });
    } else if (status === 401) {
      toast({
        title: "🔒 Authentication Error (401)",
        description: `${apiName}${endpointInfo}: ${errorMessage}`,
        variant: "destructive",
      });
    } else if (status === 403) {
      toast({
        title: "🚫 Permission Error (403)",
        description: `${apiName}${endpointInfo}: ${errorMessage}`,
        variant: "destructive",
      });
    } else if (status === 404) {
      toast({
        title: "🔍 Not Found (404)",
        description: `${apiName}${endpointInfo}: ${errorMessage}`,
        variant: "destructive",
      });
    } else if (status >= 400) {
      toast({
        title: "⚠️ Client Error",
        description: `${apiName}${endpointInfo}: ${errorMessage}`,
        variant: "destructive",
      });
    }
  }

  /**
   * Wrapper for fetch that automatically handles errors
   * @param url - The URL to fetch
   * @param options - Fetch options
   * @param apiName - Name of the API for debugging
   * @param endpoint - The endpoint being called for debugging
   * @param suppressToastForStatus - Array of status codes to suppress toast for (e.g., [404])
   * @param treat401AsSessionExpiry - Pass false for services that don't accept the back-office session
   *   (e.g. Kiosk API admin endpoints): their 401 throws ApiUnauthorizedError instead of waiting for a
   *   new token and logging the user out
   */
  static async fetchWithErrorHandling(
    url: string,
    options: RequestInit,
    apiName: string,
    endpoint?: string,
    suppressToastForStatus?: number[],
    treat401AsSessionExpiry = true
  ): Promise<Response> {
    const response = await fetch(url, options);

    if (response.status === 401 && !treat401AsSessionExpiry) {
      throw new ApiUnauthorizedError(apiName, endpoint);
    }

    if (!response.ok) {
      try {
        const errorMessage = await this.handleErrorResponse(response, apiName, endpoint, suppressToastForStatus);
        throw this.createHttpError(errorMessage, response.status);
      } catch (error) {
        if (error instanceof TokenRefreshError) {
          // Token was refreshed, retry the request with new headers
          console.log('🔄 Retrying request with refreshed token...');
          const services = servicesContainer.getServices();
          const newHeaders = await services.tokenManager.getAuthHeaders();
          const newOptions: RequestInit = {
            ...options,
            headers: {
              ...options.headers,
              ...newHeaders,
            },
          };
          const retryResponse = await fetch(url, newOptions);
          if (!retryResponse.ok) {
            // If retry also fails, handle error normally (don't wait again)
            const retryErrorMessage = await this.handleRetryErrorResponse(retryResponse, apiName, endpoint);
            throw this.createHttpError(retryErrorMessage, retryResponse.status);
          }
          return retryResponse;
        }
        throw error;
      }
    }

    return response;
  }

  /**
   * Build an Error that keeps the HTTP status, so callers can tell e.g. a timeout from other failures
   */
  private static createHttpError(message: string, status: number): Error & { status: number } {
    const error: Error & { status: number } = new Error(message) as Error & { status: number };
    error.status = status;
    return error;
  }

  /**
   * Handle error response for retry attempts (no token wait)
   */
  private static async handleRetryErrorResponse(
    response: Response,
    apiName: string,
    endpoint?: string
  ): Promise<string> {
    const errorData = await response.json().catch(() => null);
    let errorMessage = `${apiName} request failed`;

    if (errorData?.message) {
      errorMessage = errorData.message;
    } else if (errorData?.errorMessage) {
      errorMessage = errorData.errorMessage;
    } else if (response.status === 401) {
      errorMessage = 'Session expired. Please login again.';
      await this.handleExpiredToken();
    } else if (response.status === 403) {
      errorMessage = 'Access denied. Insufficient permissions.';
    } else if (response.status === 404) {
      errorMessage = 'Endpoint not found.';
    } else if (response.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    if (appConfig.isDevelopment()) {
      this.showDebugToast(response.status, errorMessage, apiName, endpoint);
    }

    return errorMessage;
  }

  /**
   * Show success toast in development mode
   * @param apiName - Name of the API
   * @param operation - The operation that succeeded
   */
  static showSuccessToast(apiName: string, operation: string): void {
    if (appConfig.isDevelopment()) {
      toast({
        title: "✅ API Success",
        description: `${apiName}: ${operation}`,
        variant: "default",
      });
    }
  }

  /**
   * Handle expired token - behavior differs by environment:
   * - Localhost/Development: Save URL and redirect to login (standalone mode)
   * - QA/Production: Clear tokens and reload page (iframe mode - parent handles auth)
   *
   * This is called automatically when a 401 error is received and token refresh fails
   */
  private static async handleExpiredToken(): Promise<void> {
    try {
      const services = servicesContainer.getServices();
      await services.tokenManager.clearTokens();

      // Check if running on localhost (standalone mode)
      const isLocalhost = window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

      if (isLocalhost) {
        // Localhost: Save current page and redirect to login
        console.warn('⚠️ Token expired (localhost) - saving current page and redirecting to login');

        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/login' && currentPath !== '/') {
          localStorage.setItem('returnUrl', currentPath);
          console.log(`💾 Saved return URL: ${currentPath}`);
        }

        // Show notification to user
        toast({
          title: "🔒 Session Expired",
          description: "Your session has expired. Redirecting to login...",
          variant: "destructive",
        });

        // Navigate to login page after a brief delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        // QA/Production (iframe mode): Reload page (parent iframe will handle auth)
        console.warn('⚠️ Token expired (iframe mode) - clearing storage and refreshing page');

        toast({
          title: "🔒 Session Expired",
          description: "Your session has expired. Refreshing page...",
          variant: "destructive",
        });

        // Auto-refresh the page after a brief delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to clear expired tokens:', error);

      // Fallback: use same environment-based logic even on error
      const isLocalhost = window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

      if (isLocalhost) {
        window.location.href = '/login';
      } else {
        window.location.reload();
      }
    }
  }
}